import React, { useMemo } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { formatDateVN } from "../../../utils/formatUtils";

/**
 * Khóa ngày làm việc dạng YYYY-MM-DD.
 * Không lấy 10 ký tự đầu của chuỗi ISO (vd. 2026-04-23T17:00:00.000Z → sai ngày so với VN+7).
 */
export function workDateKey(wd) {
  if (wd == null || wd === "") return "";
  if (wd instanceof Date) {
    if (Number.isNaN(wd.getTime())) return "";
    const y = wd.getFullYear();
    const m = String(wd.getMonth() + 1).padStart(2, "0");
    const day = String(wd.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const raw = String(wd).trim();
  const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnly) return dateOnly[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatYmdKeyVN(ymdKey) {
  const m = String(ymdKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return formatDateVN(ymdKey);
  const [, y, mo, d] = m;
  return `${d} Thg${mo} ${y}`;
}

function normalizeLocationString(loc) {
  if (loc == null || loc === "") return "";
  if (typeof loc === "object" && loc !== null && !Array.isArray(loc)) {
    const lat = loc.lat ?? loc.latitude;
    const lon = loc.lon ?? loc.lng ?? loc.longitude;
    if (lat != null && lon != null) return `${String(lat).trim()}, ${String(lon).trim()}`;
  }
  return String(loc).trim();
}

function hasValidGpsLikeAttendanceTable(loc) {
  const s = normalizeLocationString(loc).trim();
  return Boolean(s && s !== "Không xác định" && s.includes(","));
}

function formatTimeCell(t) {
  if (!t) return "—";
  return String(t).slice(0, 5);
}

function isLocationComplete(logs) {
  if (!logs?.length) return false;
  return logs.every(
    (l) =>
      hasValidGpsLikeAttendanceTable(l.check_in_location) &&
      hasValidGpsLikeAttendanceTable(l.check_out_location)
  );
}

function isAttendanceComplete(logs) {
  if (!logs?.length) return false;
  return logs.every(
    (l) => l.status !== "checkin" && l.check_out_time != null && String(l.check_out_time).trim() !== ""
  );
}

function StatusBadge({ ok, okText, badText }) {
  const good = Boolean(ok);
  return (
    <span
      className={`inline-flex rounded-lg px-2 py-0.5 text-[11px] font-black uppercase tracking-wide ${
        good ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100" : "bg-amber-50 text-amber-900 ring-1 ring-amber-100"
      }`}
    >
      {good ? okText : badText}
    </span>
  );
}

export function listDaysInMonth(yearMonth) {
  const m = String(yearMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return [];
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (mo < 1 || mo > 12) return [];
  const last = new Date(y, mo, 0).getDate();
  const pad = (n) => String(n).padStart(2, "0");
  const days = [];
  for (let d = 1; d <= last; d += 1) {
    days.push(`${m[1]}-${pad(mo)}-${pad(d)}`);
  }
  return days;
}

function leaveSessionLabel(session) {
  switch (String(session || "").toLowerCase()) {
    case "morning":
      return "Sáng";
    case "afternoon":
      return "Chiều";
    case "full":
      return "Cả ngày";
    default:
      return session ? String(session) : "";
  }
}

function leaveSessionForDay(leave, dayKey) {
  const from = workDateKey(leave.from_date);
  const to = workDateKey(leave.to_date);
  if (!from || !to || !dayKey) return leaveSessionLabel(leave.start_session || "full");
  if (from === to) {
    const s = leaveSessionLabel(leave.start_session || "full");
    const e = leaveSessionLabel(leave.end_session || leave.start_session || "full");
    if (s && e && s !== e) return `${s}–${e}`;
    return s || "Cả ngày";
  }
  if (dayKey === from) return leaveSessionLabel(leave.start_session || "full") || "Cả ngày";
  if (dayKey === to) return leaveSessionLabel(leave.end_session || "full") || "Cả ngày";
  return "Cả ngày";
}

function formatLeaveCell(leaves, dayKey) {
  if (!leaves?.length) return "—";
  return leaves
    .map((lv) => {
      const type = (lv.leave_type || "Nghỉ phép").trim();
      const session = leaveSessionForDay(lv, dayKey);
      const status = (lv.status || "").trim();
      const parts = [type];
      if (session) parts.push(session);
      if (status) parts.push(status);
      return parts.join(" · ");
    })
    .join("; ");
}

function formatOvertimeStatusLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "Đăng ký";
    case "waiting_level_2":
      return "Đang duyệt";
    case "approved":
      return "Phê duyệt";
    case "waiting_report":
      return "Chờ báo cáo";
    case "completed":
      return "Hoàn thành";
    case "waiting_review":
      return "Chờ đánh giá";
    case "cancelled":
      return "Đã hủy";
    case "rejected":
      return "Không duyệt";
    default:
      return status ? String(status) : "";
  }
}

function formatOvertimeCell(overtimes) {
  if (!overtimes?.length) return "—";
  return overtimes
    .map((ot) => {
      const slot = `${formatTimeCell(ot.ot_from)} → ${formatTimeCell(ot.ot_to)}`;
      const mins = Number(ot.ot_minutes);
      const minsLabel = Number.isFinite(mins) && mins > 0 ? `${mins}p` : "";
      const status = formatOvertimeStatusLabel(ot.status);
      return [slot, minsLabel, status].filter(Boolean).join(" · ");
    })
    .join("; ");
}

function expandLeavesByDay(leaves, monthDays) {
  const daySet = new Set(monthDays);
  const map = new Map();
  (leaves || []).forEach((leave) => {
    const from = workDateKey(leave.from_date);
    const to = workDateKey(leave.to_date);
    if (!from || !to) return;
    const start = from <= to ? from : to;
    const end = from <= to ? to : from;
    let cursor = start;
    while (cursor <= end) {
      if (daySet.has(cursor)) {
        if (!map.has(cursor)) map.set(cursor, []);
        map.get(cursor).push(leave);
      }
      const [y, m, d] = cursor.split("-").map((n) => parseInt(n, 10));
      const next = new Date(y, m - 1, d + 1);
      cursor = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(
        next.getDate()
      ).padStart(2, "0")}`;
    }
  });
  return map;
}

export function buildMonthSheetRows(monthValue, overview) {
  const days = listDaysInMonth(monthValue);
  const logsByDay = new Map();
  (overview?.logs || []).forEach((log) => {
    const key = workDateKey(log.work_date);
    if (!key) return;
    if (!logsByDay.has(key)) logsByDay.set(key, []);
    logsByDay.get(key).push(log);
  });

  const leavesByDay = expandLeavesByDay(overview?.leaves || [], days);
  const otByDay = new Map();
  (overview?.overtimes || []).forEach((ot) => {
    const key = workDateKey(ot.work_date);
    if (!key) return;
    if (!otByDay.has(key)) otByDay.set(key, []);
    otByDay.get(key).push(ot);
  });

  return days.map((dayKey) => ({
    dayKey,
    logs: logsByDay.get(dayKey) || [],
    leaves: leavesByDay.get(dayKey) || [],
    overtimes: otByDay.get(dayKey) || [],
  }));
}

export function suggestExplanationType(logs) {
  if (!logs?.length) return "missing_day";
  const hasComplete = logs.some(
    (l) => l.status !== "checkin" && l.check_out_time != null && String(l.check_out_time).trim() !== ""
  );
  const hasOpen = logs.some(
    (l) =>
      l.status === "checkin" || l.check_out_time == null || String(l.check_out_time).trim() === ""
  );
  if (hasOpen && !hasComplete) return "missing_checkout";
  if (!hasComplete) return "missing_day";
  return "other";
}

export function emptyDraftExplanation(logs) {
  return {
    reason: "",
    explanation_type: suggestExplanationType(logs),
    proposed_check_in: "",
    proposed_check_out: "",
    review_status: null,
    review_note: null,
  };
}

export function mapApiExplanationToDraft(ex, logs) {
  const base = emptyDraftExplanation(logs);
  if (!ex) return base;
  return {
    reason: ex.reason || "",
    explanation_type: ex.explanation_type || base.explanation_type,
    proposed_check_in: String(ex.proposed_check_in || "").slice(0, 5),
    proposed_check_out: String(ex.proposed_check_out || "").slice(0, 5),
    review_status: ex.review_status || null,
    review_note: ex.review_note || null,
  };
}

function explanationTypeLabel(type) {
  switch (type) {
    case "missing_checkout":
      return "Bổ sung giờ ra";
    case "missing_day":
      return "Bổ sung ngày công";
    default:
      return "Giải trình / ghi chú";
  }
}

function reviewStatusBadge(status) {
  if (status === "approved") {
    return (
      <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-900">
        Đã duyệt
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-red-900">
        Không duyệt
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-900">
        Chờ duyệt
      </span>
    );
  }
  return null;
}

function getDraft(draftReasons, dayKey, logs) {
  const raw = draftReasons?.[dayKey];
  if (raw && typeof raw === "object") return raw;
  if (typeof raw === "string") {
    return { ...emptyDraftExplanation(logs), reason: raw };
  }
  return emptyDraftExplanation(logs);
}

/**
 * Bảng phiếu công theo tháng — dùng cho NV (có giải trình) và HCNS (chỉ xem / duyệt giải trình).
 */
export default function AttendanceMonthSheetView({
  monthValue,
  overview,
  loading = false,
  editable = false,
  draftReasons = {},
  /** (dayKey, patch) => void */
  onDraftChange,
  explanationPlaceholder = "",
  canExplain = false,
  canConfirmWrite = false,
  canConfirmDelete = false,
  hasSavedExplanationOnServer,
  savingDay = null,
  deleteKey = null,
  /** Prefixo khóa xóa: "own" hoặc "hr:<employeeId>" → khớp `deleteKey` */
  deleteKeyPrefix = "own",
  /** HCNS xem: cho phép xóa giải trình dù không editable */
  allowHrDelete = false,
  /** HCNS: duyệt / từ chối giải trình chờ xử lý */
  allowHrReview = false,
  reviewingDay = null,
  onReviewExplanation,
  /** Tô nền ngày có giải trình */
  highlightExplanations = false,
  onSaveExplanation,
  onDeleteExplanation,
  emptyHint = null,
}) {
  const rows = useMemo(() => buildMonthSheetRows(monthValue, overview), [monthValue, overview]);
  const savedSet = hasSavedExplanationOnServer || new Set();

  if (loading && !overview) {
    return (
      <div className="flex justify-center py-10 text-gray-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-2 text-sm leading-relaxed">
        <p className="font-semibold text-gray-600">
          {emptyHint || `Tháng áp dụng không hợp lệ (${monthValue}).`}
        </p>
      </div>
    );
  }

  const hasExplanation = (dayKey, logs) => {
    if (savedSet.has(dayKey)) return true;
    const d = getDraft(draftReasons, dayKey, logs);
    return Boolean(String(d.reason || "").trim());
  };

  const renderExplanationReadOnly = (dayKey, logs) => {
    const d = getDraft(draftReasons, dayKey, logs);
    const text = String(d.reason || "").trim();
    if (!text && !savedSet.has(dayKey)) return <span className="text-gray-400">—</span>;
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-bold text-gray-500">
            {explanationTypeLabel(d.explanation_type)}
          </span>
          {reviewStatusBadge(d.review_status)}
        </div>
        {(d.proposed_check_in || d.proposed_check_out) && (
          <p className="text-xs font-semibold text-gray-700">
            Đề xuất: {d.proposed_check_in || "—"} → {d.proposed_check_out || "—"}
          </p>
        )}
        {text ? (
          <p className="whitespace-pre-wrap break-words rounded-lg bg-amber-50/80 px-2.5 py-1.5 text-sm text-amber-950 ring-1 ring-amber-100">
            {text}
          </p>
        ) : null}
        {d.review_status === "rejected" && d.review_note ? (
          <p className="text-xs text-red-800">
            <span className="font-semibold">Lý do HCNS:</span> {d.review_note}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          {allowHrReview && d.review_status === "pending" && savedSet.has(dayKey) ? (
            <>
              <button
                type="button"
                onClick={() => onReviewExplanation?.(dayKey, "approved")}
                disabled={reviewingDay === dayKey}
                className="inline-flex min-h-[2.25rem] items-center rounded-lg bg-emerald-700 px-3 text-[11px] font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                {reviewingDay === dayKey ? "..." : "Duyệt & áp số liệu"}
              </button>
              <button
                type="button"
                onClick={() => onReviewExplanation?.(dayKey, "rejected")}
                disabled={reviewingDay === dayKey}
                className="inline-flex min-h-[2.25rem] items-center rounded-lg border border-red-200 bg-white px-3 text-[11px] font-bold text-red-800 hover:bg-red-50 disabled:opacity-50"
              >
                Không duyệt
              </button>
            </>
          ) : null}
          {allowHrDelete && canConfirmDelete ? (
            <button
              type="button"
              title="Xóa giải trình"
              aria-label="Xóa giải trình"
              onClick={() => onDeleteExplanation?.(dayKey)}
              disabled={deleteKey === `${deleteKeyPrefix}:${dayKey}`}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-800 disabled:opacity-50"
            >
              {deleteKey === `${deleteKeyPrefix}:${dayKey}` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" strokeWidth={2.2} />
              )}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderExplanation = (dayKey, logs) => {
    if (!editable) return renderExplanationReadOnly(dayKey, logs);

    const d = getDraft(draftReasons, dayKey, logs);
    const locked = d.review_status === "approved" || !canExplain || !canConfirmWrite;
    const type = d.explanation_type || "other";

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {reviewStatusBadge(d.review_status)}
          {d.review_status === "rejected" && d.review_note ? (
            <span className="text-[11px] font-semibold text-red-700">HCNS: {d.review_note}</span>
          ) : null}
        </div>
        <select
          value={type}
          disabled={locked}
          onChange={(e) => onDraftChange?.(dayKey, { explanation_type: e.target.value })}
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 outline-none focus:border-red-400 focus:bg-white disabled:opacity-60"
        >
          <option value="missing_checkout">Bổ sung giờ ra (thiếu checkout)</option>
          <option value="missing_day">Bổ sung ngày công (quên chấm)</option>
          <option value="other">Giải trình / ghi chú khác</option>
        </select>
        {type === "missing_checkout" ? (
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500">
            Giờ ra đề xuất
            <input
              type="time"
              value={d.proposed_check_out || ""}
              disabled={locked}
              onChange={(e) => onDraftChange?.(dayKey, { proposed_check_out: e.target.value })}
              className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-red-400 disabled:opacity-60"
            />
          </label>
        ) : null}
        {type === "missing_day" ? (
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500">
              Giờ vào
              <input
                type="time"
                value={d.proposed_check_in || ""}
                disabled={locked}
                onChange={(e) => onDraftChange?.(dayKey, { proposed_check_in: e.target.value })}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-red-400 disabled:opacity-60"
              />
            </label>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500">
              Giờ ra
              <input
                type="time"
                value={d.proposed_check_out || ""}
                disabled={locked}
                onChange={(e) => onDraftChange?.(dayKey, { proposed_check_out: e.target.value })}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-red-400 disabled:opacity-60"
              />
            </label>
          </div>
        ) : null}
        <textarea
          rows={2}
          value={d.reason || ""}
          onChange={(e) => onDraftChange?.(dayKey, { reason: e.target.value })}
          disabled={locked}
          placeholder={explanationPlaceholder}
          className="w-full min-h-[4.5rem] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-red-400 focus:bg-white disabled:opacity-60 md:min-h-0"
        />
      </div>
    );
  };

  const renderActions = (dayKey, logs) => {
    if (!editable) return null;
    const d = getDraft(draftReasons, dayKey, logs);
    const locked = d.review_status === "approved";
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => onSaveExplanation?.(dayKey)}
          disabled={locked || !canExplain || !canConfirmWrite || savingDay === dayKey}
          className="min-h-[2.75rem] flex-1 rounded-xl bg-red-600 px-4 text-xs font-bold text-white hover:bg-red-700 disabled:bg-gray-400 sm:flex-none sm:rounded-lg sm:px-3 sm:py-2"
        >
          {savingDay === dayKey ? "Đang lưu..." : "Lưu"}
        </button>
        {canConfirmDelete && canExplain && savedSet.has(dayKey) && !locked ? (
          <button
            type="button"
            title="Xóa giải trình đã lưu"
            aria-label="Xóa giải trình"
            onClick={() => onDeleteExplanation?.(dayKey)}
            disabled={deleteKey === `${deleteKeyPrefix}:${dayKey}`}
            className="inline-flex h-11 min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white p-2 text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-800 disabled:opacity-50 sm:h-auto sm:rounded-lg"
          >
            {deleteKey === `${deleteKeyPrefix}:${dayKey}` ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" strokeWidth={2.2} />
            )}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="touch-pan-y space-y-3 md:hidden">
        {rows.map(({ dayKey, logs, leaves, overtimes }) => {
          const hasLogs = logs.length > 0;
          const explained = hasExplanation(dayKey, logs);
          const slots = hasLogs
            ? logs
                .map((l) => `${formatTimeCell(l.check_in_time)} → ${formatTimeCell(l.check_out_time)}`)
                .join("; ")
            : "—";
          const locOk = isLocationComplete(logs);
          const attOk = isAttendanceComplete(logs);
          return (
            <div
              key={dayKey}
              className={`rounded-xl border p-3 shadow-sm ${
                highlightExplanations && explained
                  ? "border-amber-200 bg-amber-50/70"
                  : "border-gray-100 bg-gray-50/90"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-200/80 pb-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-bold text-gray-900">{formatYmdKeyVN(dayKey)}</span>
                  {highlightExplanations && explained ? (
                    <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-900">
                      Có giải trình
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {hasLogs ? (
                    <>
                      <StatusBadge ok={locOk} okText="Đủ định vị" badText="Thiếu định vị" />
                      <StatusBadge ok={attOk} okText="Đủ checkout" badText="Thiếu checkout" />
                    </>
                  ) : (
                    <span className="inline-flex rounded-lg px-2 py-0.5 text-[11px] font-semibold text-gray-500 ring-1 ring-gray-200">
                      Không chấm công
                    </span>
                  )}
                </div>
              </div>
              <p className="mt-2 break-words text-sm text-gray-600">
                <span className="font-semibold text-gray-500">Giờ vào / ra:</span> {slots}
              </p>
              <p className="mt-1 break-words text-sm text-gray-600">
                <span className="font-semibold text-gray-500">Nghỉ phép:</span>{" "}
                {formatLeaveCell(leaves, dayKey)}
              </p>
              <p className="mt-1 break-words text-sm text-gray-600">
                <span className="font-semibold text-gray-500">Tăng ca:</span> {formatOvertimeCell(overtimes)}
              </p>
              <label className="mt-3 block text-[11px] font-bold uppercase tracking-wide text-gray-500">
                Giải trình
              </label>
              <div className="mt-1">{renderExplanation(dayKey, logs)}</div>
              {editable ? <div className="mt-2">{renderActions(dayKey, logs)}</div> : null}
            </div>
          );
        })}
      </div>

      <div className="hidden md:block md:overflow-x-auto md:rounded-lg">
        <table className="min-w-[980px] w-full table-fixed text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] font-black uppercase tracking-wide text-gray-400">
              <th className="w-[7.5rem] py-2 pr-3">Ngày</th>
              <th className="min-w-[7rem] py-2 pr-3">Giờ vào / ra</th>
              <th className="w-[9rem] py-2 pr-3 whitespace-normal">Định vị</th>
              <th className="w-[9rem] py-2 pr-3 whitespace-normal">Chấm công</th>
              <th className="min-w-[8rem] py-2 pr-3">Nghỉ phép</th>
              <th className="min-w-[8rem] py-2 pr-3">Tăng ca</th>
              <th className="py-2 pr-3">Giải trình</th>
              {editable ? <th className="w-[8.5rem] py-2">Thao tác</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ dayKey, logs, leaves, overtimes }) => {
              const hasLogs = logs.length > 0;
              const explained = hasExplanation(dayKey, logs);
              const slots = hasLogs
                ? logs
                    .map((l) => `${formatTimeCell(l.check_in_time)} → ${formatTimeCell(l.check_out_time)}`)
                    .join("; ")
                : "—";
              const locOk = isLocationComplete(logs);
              const attOk = isAttendanceComplete(logs);
              return (
                <tr
                  key={dayKey}
                  className={`border-b align-top ${
                    highlightExplanations && explained
                      ? "border-amber-100 bg-amber-50/50"
                      : "border-gray-50"
                  }`}
                >
                  <td className="py-3 pr-3 font-bold text-gray-800 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span>{formatYmdKeyVN(dayKey)}</span>
                      {highlightExplanations && explained ? (
                        <span className="w-fit rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-900">
                          Có giải trình
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="break-words py-3 pr-3 text-gray-600">{slots}</td>
                  <td className="py-3 pr-3">
                    {hasLogs ? (
                      <StatusBadge ok={locOk} okText="Đủ định vị" badText="Thiếu định vị" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-3">
                    {hasLogs ? (
                      <StatusBadge ok={attOk} okText="Đủ checkout" badText="Thiếu checkout" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="break-words py-3 pr-3 text-gray-600">
                    {formatLeaveCell(leaves, dayKey)}
                  </td>
                  <td className="break-words py-3 pr-3 text-gray-600">
                    {formatOvertimeCell(overtimes)}
                  </td>
                  <td className="py-3 pr-3">{renderExplanation(dayKey, logs)}</td>
                  {editable ? <td className="py-3">{renderActions(dayKey, logs)}</td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
